import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { usePageMeta } from '../hooks/usePageMeta';
import SafeIcon from '../common/SafeIcon';
import ScrollToTop from '../components/ScrollToTop';
import Reveal from '../components/Reveal';
import Hero from '../components/Hero';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiArrowRight, FiCheck, FiUser } = FiIcons;

// Try to import the portrait image, use placeholder if not available
let portraitImage;
try {
  portraitImage = new URL('../assets/images/SörenPadel_3.webp', import.meta.url).href;
} catch (e) {
  portraitImage = null;
}

const About = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage].about;
  usePageMeta({ title: t.title, description: t.intro, path: 'about' });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <Hero gradientClass="hero-gradient-about" title={t.title} />

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Introduction with Portrait */}
          <div className="flex flex-col md:flex-row gap-8 mb-16">
            <Reveal direction="right" className="md:w-1/3">
              {portraitImage ? (
                <img
                  src={portraitImage}
                  alt="Sören Padel"
                  className="rounded-sm shadow-lg w-full max-w-sm mx-auto"
                />
              ) : (
                <div className="rounded-sm shadow-lg w-full max-w-sm mx-auto bg-gradient-to-br from-brand to-navy-800 aspect-[3/4] flex items-center justify-center">
                  <SafeIcon icon={FiUser} className="h-24 w-24 text-amber-300" />
                </div>
              )}
            </Reveal>
            <Reveal direction="left" delay={0.15} className="md:w-2/3">
              <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4">{t.greeting}</h2>
              <p className="text-lg text-stone-700 leading-relaxed mb-4">
                {t.intro}
              </p>
              <p className="text-stone-500 italic">
                {t.aliteroMeaning}
              </p>
            </Reveal>
          </div>

          {/* Background Section */}
          <Reveal className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-stone-900 mb-6 pb-3 border-b-2 border-amber-600">
              {t.background.title}
            </h2>
            <p className="text-stone-700 leading-relaxed mb-6">
              {t.background.intro}
            </p>
            <ul className="space-y-3 mb-6">
              {t.background.roles.map((role, index) => (
                <Reveal key={index} as="li" delay={index * 0.08} className="flex items-start list-none">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-amber-700 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-stone-700">{role}</span>
                </Reveal>
              ))}
            </ul>
            <p className="text-stone-700 leading-relaxed">
              {t.background.summary}
            </p>
          </Reveal>

          {/* Tools Section */}
          <Reveal className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-stone-900 mb-6 pb-3 border-b-2 border-amber-600">
              {t.tools.title}
            </h2>
            <p className="text-stone-700 leading-relaxed mb-6">
              {t.tools.intro}
            </p>
            <ul className="space-y-3 mb-6">
              {t.tools.items && t.tools.items.map((item, index) => (
                <Reveal key={index} as="li" delay={index * 0.08} className="flex items-start list-none">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-amber-700 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-stone-700"><strong>{item.category}:</strong> {item.tool}</span>
                </Reveal>
              ))}
            </ul>
            <p className="text-stone-700 leading-relaxed">
              {t.tools.conclusion}
            </p>
          </Reveal>

          {/* Personal Section */}
          <Reveal className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-stone-900 mb-6 pb-3 border-b-2 border-amber-600">
              {t.personal.title}
            </h2>
            <p className="text-stone-700 mb-4">{t.personal.intro}</p>
            <ul className="space-y-3 mb-6">
              {t.personal.hobbies.map((hobby, index) => (
                <Reveal key={index} as="li" delay={index * 0.08} className="flex items-start list-none">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-amber-700 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-stone-700">{hobby}</span>
                </Reveal>
              ))}
            </ul>
            <p className="text-stone-700 leading-relaxed">{t.personal.conclusion}</p>
          </Reveal>

          {/* CTA Section */}
          <Reveal className="bg-stone-100 rounded-sm p-8 text-center border border-stone-200">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4">{t.cta.welcome}</h2>
            <p className="text-stone-700 mb-6 max-w-2xl mx-auto">{t.cta.message}</p>
            <p className="text-stone-500 mb-6">{t.cta.signature}</p>
            <Link
              to="/request"
              className="inline-flex items-center px-8 py-4 bg-amber-700 text-white font-semibold rounded-sm hover:bg-amber-800 transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              <SafeIcon icon={FiMail} className="mr-2 h-5 w-5" />
              {t.cta.button}
              <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
            </Link>
          </Reveal>
        </div>
      </section>
      <ScrollToTop />
    </div>
  );
};

export default About;
